import { create } from 'zustand';
import { partnerThemes } from '../theme/partnerThemes';
import defaultTheme from '../theme/antdTheme';
import {
  applyBrandTokens,
  buildBrandRamp,
  mix,
  rgbTriple,
  withBrandingFallbacks,
} from '../theme/brandTokens';
import { getBootBranding, isDemoBrandingActive, loadPortalBranding } from '../theme/brandingSource';

/**
 * THE PORTAL'S THEME — now derived from the SAVED BRANDING, not a static map.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Before this change the store built its antd config from `partnerThemes`, a
 * two-entry hardcoded map. The colours a partner actually saved on Settings ->
 * Branding lived in a DIFFERENT store (`usePartnerStore.brandingOverrides`) and
 * reached only that page's own preview card — so saving a brand repainted a
 * thumbnail and left the console it was supposed to brand untouched.
 *
 * `branding` is now the single input and everything else is derived from it:
 *   - `themeConfig`  -> antd, which repaints every component from `colorPrimary`
 *   - `applyBrandTokens` -> the `--pa-brand-*` variables, for our own styles
 *
 * BOTH ARE WRITTEN FROM `setBranding` AND NOWHERE ELSE, so the two channels
 * cannot drift apart. The CSS variables are also applied at module load, below,
 * so the first paint is already branded rather than flashing the default first.
 */

/**
 * The antd `ConfigProvider` theme for a resolved branding.
 *
 * REAL HEX VALUES ONLY — antd derives its entire palette (hover, active,
 * disabled, focus ring, ten-step ramp) from `token.colorPrimary` by running a
 * colour algorithm over it at runtime. Handing it `var(--pa-brand-primary)`
 * would make every one of those derivations produce garbage, which is why the
 * CSS custom properties are a parallel channel rather than the only one.
 */
export function buildThemeConfig(branding) {
  const b = withBrandingFallbacks(branding);
  const primary = b.primaryColor;
  const ramp = buildBrandRamp(primary);
  const rgb = rgbTriple(primary);

  return {
    ...defaultTheme,
    token: {
      ...defaultTheme.token,
      colorPrimary: primary,
      /**
       * LINKS FOLLOW THE BRAND — this is why every `<a>` in the portal was blue.
       *
       * antd derives `colorLink` from `colorInfo` when it is not set, and
       * `antdTheme.js` pins `colorInfo` to #3B82F6 for informational states. So
       * every link — booking references in Communications, table links, anchors
       * throughout — came out that blue regardless of the partner's palette.
       * Hover and active are set too; leaving them unset would have left them
       * deriving from `colorInfo` and the link would change hue on hover.
       *
       * `colorInfo` itself is deliberately NOT touched: it is the semantic
       * "information" colour for alerts and badges, not a brand slot.
       */
      colorLink: primary,
      colorLinkHover: mix(primary, 0.2),
      colorLinkActive: mix(primary, -0.2),
      // The branding page's font dropdown rides the same save as the colours.
      // Quoted because a family name can contain spaces ("DM Sans").
      fontFamily: `'${b.fontFamily}'`,
    },
    components: {
      ...defaultTheme.components,
      /**
       * LAYOUT AND MENU ARE LEFT AT THE DEFAULTS — product decision that the
       * left sidebar does not follow the brand. They used to derive `siderBg`
       * and the menu's selected/hover tints from the primary; spreading
       * `defaultTheme` alone now keeps antd on the shipped #1E1B3A panel and the
       * #7C5CFC selection, for every partner. See the note in AppLayout.jsx.
       */
      Layout: { ...defaultTheme.components.Layout },
      Menu: { ...defaultTheme.components.Menu },
      // These three carried literal #7C5CFC in antdTheme.js. Left there as the
      // no-branding default; overridden here so they follow the brand.
      Tabs: {
        ...defaultTheme.components.Tabs,
        inkBarColor: primary,
      },
      Input: {
        ...defaultTheme.components.Input,
        hoverBorderColor: primary,
        activeShadow: `0 0 0 2px rgba(${rgb}, 0.08)`,
      },
      Progress: {
        ...defaultTheme.components.Progress,
        defaultColor: primary,
      },
    },
    // Carried alongside the antd config so a consumer that needs a real colour
    // value — a chart series, a canvas fill, an inline SVG — can read one
    // without re-deriving the ramp.
    brand: { ...b, ramp },
  };
}

/**
 * Field-by-field equality over the resolved branding.
 *
 * The object is flat and nine keys wide, so this is cheaper than the re-render
 * it prevents. Compared AFTER `withBrandingFallbacks` on both sides, so "field
 * absent" and "field explicitly the default" correctly compare equal — which is
 * exactly the shape a §30.1 refresh takes when nothing has been saved.
 */
const BRANDING_KEYS = [
  'name', 'primaryColor', 'secondaryColor', 'accentColor',
  'fontFamily', 'logoUrl', 'logoDarkUrl', 'iconUrl', 'faviconUrl',
];

const sameBranding = (a, b) => BRANDING_KEYS.every((k) => a?.[k] === b?.[k]);

const BOOT_BRANDING = withBrandingFallbacks(getBootBranding());

// Applied at MODULE LOAD, before React's first render, so the console's first
// paint is already the partner's. Doing it in an effect is what produced the
// visible default-then-brand flash this store exists to avoid.
applyBrandTokens(BOOT_BRANDING);

const useThemeStore = create((set, get) => ({
  activePartnerId: 'aldar',
  branding: BOOT_BRANDING,
  themeConfig: buildThemeConfig(BOOT_BRANDING),

  /**
   * Merge a branding patch and repaint.
   *
   * PARTIAL BY DESIGN. The branding page fires this once per colour-picker
   * change with a single key (`{ primaryColor }`), so the console repaints live
   * as the partner drags the picker rather than only on Save. Merging also means
   * a §30.1 response missing a field cannot wipe the rest of the brand.
   */
  setBranding: (patch) => {
    const merged = withBrandingFallbacks({ ...get().branding, ...(patch ?? {}) });
    // NO-OP WHEN NOTHING CHANGED. `buildThemeConfig` allocates a fresh nested
    // object, and handing antd a new `theme` identity makes it recompute its
    // whole derived palette and re-render every component under the provider.
    // The common cases — the §30.1 refresh agreeing with the cache, a repeated
    // logo publish — must not pay for that.
    if (sameBranding(get().branding, merged)) return;
    applyBrandTokens(merged);
    set({ branding: merged, themeConfig: buildThemeConfig(merged) });
  },

  /**
   * Refresh from §30.1 — call once, after sign-in.
   *
   * `loadPortalBranding` de-duplicates, so this is safe to call from more than
   * one place and from an effect that may run twice under StrictMode. A `null`
   * result means "nothing to apply" (signed out, request failed, or a partner
   * who has never saved), and is deliberately NOT treated as "reset to
   * default" — the console keeps whatever is already correct on screen.
   */
  hydrateBranding: async () => {
    const fresh = await loadPortalBranding();
    if (fresh) get().setBranding(fresh);
    return fresh;
  },

  /**
   * Adopt a partner from `partnerThemes` — the login screen's path.
   *
   * ── WHY THIS NO LONGER SETS COLOURS ─────────────────────────────────────
   * `partnerThemes` is a two-entry map of HARDCODED demo colours that predates
   * dynamic branding, and `finishSignIn` calls this on every login. While it
   * still pushed that block into the theme, the sequence was:
   *
   *     boot -> branding applied      (correct colours)
   *     login -> setPartner('aldar')  -> #7C5CFC forced back over the top
   *
   * which is precisely why a static branding change looked like it did nothing:
   * it WAS applied, and then silently overwritten a second later. Colours must
   * come from the branding — the saved one, or the default — and from nowhere
   * else, so this now sets only the identity the rest of the app keys off
   * (`usePartnerCurrency` and `PartnerHeader` read `activePartnerId`).
   *
   * The `name` is still adopted, because it is the signed-in partner's real
   * name and the sidebar falls back to it when no logo is uploaded — but ONLY
   * when a demo brand is not forcing the theme, so a demo cannot be half
   * overwritten and end up showing one brand's name in another's colours.
   */
  setPartner: (partnerId) => {
    const partner = partnerThemes[partnerId];
    if (!partner) return;
    set({ activePartnerId: partnerId });
    if (!isDemoBrandingActive() && partner.name) get().setBranding({ name: partner.name });
  },
}));

export default useThemeStore;
